import React from 'react';
import { compose } from 'redux';
import { string, func } from 'prop-types';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

// Import util modules
import { intlShape, injectIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { normalizePhoneNumber, receivesOrderTextsAt } from '../../../util/phoneNumber';

// Import shared components
import {
  Form,
  Button,
  FieldCheckbox,
  FieldSelect,
  FieldTextInput,
  PrimaryButton,
} from '../../../components';
import css from './ShippingAddressForm.module.css';
import { useConfiguration } from '../../../context/configurationContext';
import getCountryCodes from '../../../translations/countryCodes';

const DEFAULT_PHONE_COUNTRY = 'US';

const ShippingAddressFormComponent = props => {
  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          className,
          handleSubmit,
          intl,
          form,
          values,
          rootClassName,
          showShippingAddressForm,
          setShowShippingAddressForm,
          inProgress,
          currentUser,
          showTextOptIn = false,
          shippingError,
          saveInProgress,
        } = formRenderProps;
        const classes = classNames(rootClassName || css.root, className);
        const config = useConfiguration();
        const countryCodes = getCountryCodes(config.localization.locale);
        // The phone goes on the shipping label, so it must be a real number; no code needed.
        const normalizedFormPhone = normalizePhoneNumber(values?.phoneNumber || '');
        const phoneNumberInvalid =
          Boolean(values?.phoneNumber) && !isValidPhoneNumber(normalizedFormPhone);
        const phoneNumberReady = Boolean(values?.phoneNumber) && !phoneNumberInvalid;
        const alreadyGetsOrderTexts =
          phoneNumberReady && receivesOrderTextsAt(currentUser, normalizedFormPhone);
        const optionalText = intl.formatMessage({
          id: 'ShippingDetails.optionalText',
        });

        return (
          <Form
            className={classes}
            onSubmit={e => {
              if (!phoneNumberReady) {
                e.preventDefault();
                return;
              }
              handleSubmit(e);
            }}
          >
            <div className={css.useSavedAddress}>
              {showShippingAddressForm ? (
                <Button
                  className={css.useSavedBilling}
                  onClick={() => setShowShippingAddressForm(false)}
                >
                  {intl.formatMessage({ id: 'ShippingDetails.useSavedAddress' })}
                </Button>
              ) : null}

              <div className={classNames(css.formRow, css.fullWidth)}>
                <div className={css.formFld}>
                  <FieldTextInput
                    id="name"
                    name="name"
                    className={css.fieldFullWidth}
                    type="text"
                    autoComplete="shipping name"
                    label={intl.formatMessage({ id: 'ShippingDetails.recipientNameLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ShippingDetails.recipientNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ShippingDetails.recipientNameRequired' })
                    )}
                    onUnmount={() => form.change('name', undefined)}
                  />
                </div>
              </div>
              <div className={css.formRow}>
                <FieldTextInput
                  id="line1"
                  name="line1"
                  className={css.formFld}
                  type="text"
                  autoComplete="shipping address-line1"
                  label={intl.formatMessage({ id: 'ShippingDetails.addressLine1Label' })}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetails.addressLine1Placeholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.addressLine1Required' })
                  )}
                  onUnmount={() => form.change('line1', undefined)}
                />

                <FieldTextInput
                  id="line2"
                  name="line2"
                  className={css.formFld}
                  type="text"
                  autoComplete="shipping address-line2"
                  label={intl.formatMessage(
                    { id: 'ShippingDetails.addressLine2Label' },
                    { optionalText }
                  )}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetails.addressLine2Placeholder',
                  })}
                  onUnmount={() => form.change('line2', undefined)}
                />
              </div>
              <div className={css.formRow}>
                <FieldTextInput
                  id="city"
                  name="city"
                  className={css.formFld}
                  type="text"
                  autoComplete="shipping address-level2"
                  label={intl.formatMessage({ id: 'ShippingDetails.cityLabel' })}
                  placeholder={intl.formatMessage({ id: 'ShippingDetails.cityPlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.cityRequired' })
                  )}
                  onUnmount={() => form.change('city', undefined)}
                />
                <FieldTextInput
                  id="zip"
                  name="zip"
                  className={css.formFld}
                  type="text"
                  autoComplete="shipping postal-code"
                  label={intl.formatMessage({ id: 'ShippingDetails.postalCodeLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetails.postalCodePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.postalCodeRequired' })
                  )}
                  onUnmount={() => form.change('zip', undefined)}
                />
              </div>
              <div className={classNames(css.formRow, css.fullWidth)}>
                <div className={css.formFld}>
                  <label className={css.phoneNumberLabel}>
                    {intl.formatMessage({ id: 'ShippingDetails.phoneLabel' })}
                  </label>
                  <Field name="phoneNumber">
                    {({ input }) => (
                      <PhoneInput
                        {...input}
                        value={normalizePhoneNumber(input.value || '') || undefined}
                        placeholder={intl.formatMessage({
                          id: 'ShippingDetails.phonePlaceholder',
                        })}
                        defaultCountry={DEFAULT_PHONE_COUNTRY}
                      />
                    )}
                  </Field>
                  {phoneNumberInvalid ? (
                    <p className={css.invalidPhoneValue}>
                      {intl.formatMessage({ id: 'ShippingDetails.phoneNumberInvalid' })}
                    </p>
                  ) : !values?.phoneNumber ? (
                    <p className={css.phoneHint}>
                      {intl.formatMessage({ id: 'ShippingDetails.phoneRequiredHint' })}
                    </p>
                  ) : null}
                  {showTextOptIn && phoneNumberReady ? (
                    alreadyGetsOrderTexts ? (
                      <p className={css.textOptInNote}>
                        {intl.formatMessage({ id: 'ShippingDetails.textOptInAlreadyOn' })}
                      </p>
                    ) : (
                      <FieldCheckbox
                        id="textOptIn"
                        name="textOptIn"
                        className={css.textOptIn}
                        label={intl.formatMessage({ id: 'ShippingDetails.textOptInLabel' })}
                      />
                    )
                  ) : null}
                </div>
              </div>
              <div className={css.formRow}>
                <FieldTextInput
                  id="state"
                  name="state"
                  className={css.formFld}
                  type="text"
                  autoComplete="shipping address-level1"
                  label={intl.formatMessage({ id: 'ShippingDetails.stateLabel' })}
                  placeholder={intl.formatMessage({ id: 'ShippingDetails.statePlaceholder' })}
                  validate={
                    ['US', 'CA'].includes(values?.country || DEFAULT_PHONE_COUNTRY)
                      ? validators.required(
                          intl.formatMessage({ id: 'ShippingDetails.stateRequired' })
                        )
                      : undefined
                  }
                  onUnmount={() => form.change('state', undefined)}
                />

                <FieldSelect
                  id="country"
                  name="country"
                  className={css.formFld}
                  defaultValue="US"
                  label={intl.formatMessage({ id: 'ShippingDetails.countryLabel' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.countryRequired' })
                  )}
                >
                  <option disabled value="">
                    {intl.formatMessage({ id: 'ShippingDetails.countryPlaceholder' })}
                  </option>
                  {countryCodes.map(country => {
                    return (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    );
                  })}
                </FieldSelect>
              </div>
              {shippingError ? (
                <p className={css.invalidPhoneValue} role="alert">
                  {intl.formatMessage({ id: shippingError.messageId })}
                </p>
              ) : null}
              <div className={css.bottomWrapper}>
                <PrimaryButton
                  type="submit"
                  inProgress={inProgress || saveInProgress}
                  disabled={!phoneNumberReady || inProgress || saveInProgress}
                >
                  {intl.formatMessage({
                    id: 'ShippingDetails.saveAddress',
                  })}
                </PrimaryButton>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

ShippingAddressFormComponent.defaultProps = {
  className: null,
  formId: 'ShippingAddressForm',
  fetchErrors: null,
};

ShippingAddressFormComponent.propTypes = {
  className: string,
  onSubmit: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ShippingAddressForm = compose(injectIntl)(ShippingAddressFormComponent);

export default ShippingAddressForm;
